import {IconButton} from '@dropbox/dig-components/buttons';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowDownLine} from '@dropbox/dig-icons/assets';
import {ComponentProps, CSSProperties, ReactNode, useRef} from 'react';

import styles from './ChatThread.module.css';
import {useChatScroll} from './useChatScroll';

export type ChatThreadProps = {
  children: ReactNode;
  composer: ReactNode;
  header?: ReactNode;
  isStreaming?: boolean;
  hasMessages?: boolean;
  /** Serialized fingerprint of content that should trigger stick-to-bottom remeasure. */
  contentVersion?: string | number;
  backgroundColor?: 'Background Subtle' | 'Background Base' | 'Primary Surface';
  paddingX?: ComponentProps<typeof Box>['paddingX'];
  jumpToLatestLabel?: string;
  contentClassName?: string;
  /** Class applied to the composer wrapper only (e.g. to center it in a column). */
  composerClassName?: string;
};

export const ChatThread = ({
  children,
  composer,
  header,
  isStreaming = false,
  hasMessages = false,
  contentVersion = 0,
  backgroundColor = 'Background Subtle',
  paddingX = '24',
  jumpToLatestLabel = 'Scroll to latest',
  contentClassName,
  composerClassName,
}: ChatThreadProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {scrollThreadToBottom, showBelowFoldHint, showAboveFoldHint} = useChatScroll({
    scrollContainerRef,
    messagesEndRef,
    isStreaming,
    hasMessages,
    contentVersion,
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      backgroundColor={backgroundColor}
      paddingX={paddingX}
      paddingTop="8"
      paddingBottom="24"
      style={{
        flex: '1 1 0%',
        minHeight: 0,
        height: 0,
        minWidth: 0,
      }}
    >
      {header ? (
        <Box width="100%" style={{flexShrink: 0}}>
          {header}
        </Box>
      ) : null}
      <div
        className={styles.chatScrollArea}
        style={
          {
            '--chat-thread-bg':
              backgroundColor === 'Background Base'
                ? 'var(--dig-color__background__base)'
                : backgroundColor === 'Primary Surface'
                  ? 'var(--dig-color__success__on-base)'
                  : 'var(--dig-color__background__subtle)',
          } as CSSProperties
        }
      >
        <div
          className={`${styles.topScrollShadow}${
            showAboveFoldHint ? ` ${styles.topScrollShadowVisible}` : ''
          }`}
          aria-hidden
        />
        <Box
          ref={scrollContainerRef}
          className={styles.scrollViewport}
          overflowY="auto"
          display="flex"
          flexDirection="column"
          style={{
            flex: '1 1 0%',
            minHeight: 0,
            height: 0,
            minWidth: 0,
            overscrollBehavior: 'contain',
          }}
        >
          <Box display="flex" flexDirection="column" width="100%" minHeight="100%" flexShrink={0}>
            <Box aria-hidden flexGrow={1} flexShrink={1} minHeight="0" />
            <Box
              display="flex"
              flexDirection="column"
              flexShrink={0}
              className={contentClassName}
              style={{width: '100%', gap: 8}}
            >
              {children}
              <div
                ref={messagesEndRef}
                style={{height: 1, width: '100%', flexShrink: 0}}
                aria-hidden
              />
            </Box>
          </Box>
        </Box>
        <div
          className={`${styles.bottomScrollShadow}${
            showBelowFoldHint ? ` ${styles.bottomScrollShadowVisible}` : ''
          }`}
          aria-hidden
        />
        {showBelowFoldHint ? (
          <div className={styles.jumpToLatest}>
            <Tooltip placement="top" title={jumpToLatestLabel}>
              <IconButton
                size="small"
                variant="primary"
                shape="circular"
                onClick={() => scrollThreadToBottom()}
                aria-label={jumpToLatestLabel}
              >
                <UIIcon
                  src={ArrowDownLine}
                  size="small"
                  className={atoms({color: 'Background Base'})}
                />
              </IconButton>
            </Tooltip>
          </div>
        ) : null}
      </div>
      <Box className={composerClassName ?? contentClassName} style={{flexShrink: 0}}>
        {composer}
      </Box>
    </Box>
  );
};
